import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRoom } from '../components/RoomContext';
import { Plus, LogIn, Share2 } from 'lucide-react';
import { nanoid } from 'nanoid';

const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

export default function RoomEntry() {
  const [username, setUsername] = useState('');
  const [joinRoomId, setJoinRoomId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { joinRoom } = useRoom();
  const navigate = useNavigate();
  const location = useLocation();

  // If coming from a shared link, prefill joinRoomId and auto-focus join form
  const [autoJoin, setAutoJoin] = useState(false);
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const roomId = params.get('room');
    if (roomId) {
      setJoinRoomId(roomId);
      setAutoJoin(true);
    }
  }, [location.search]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Generate a unique room code (date + nanoid)
      const code = `${new Date().toISOString().slice(0,10).replace(/-/g, '')}-${nanoid(6)}`;
      const res = await fetch(`${apiUrl}/api/room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: code, createdBy: username })
      });
      if (!res.ok) throw new Error('Failed to create room');
      const room = await res.json();
      // Save username to localStorage for this room before navigating
      localStorage.setItem(`room_${room.id}_user`, JSON.stringify({ username }));
      joinRoom(room, username);
      navigate(`/room/${room.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/room/${joinRoomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username })
      });
      if (!res.ok) {
        let msg = 'Failed to join room';
        try {
          const errData = await res.json();
          msg = errData?.error || msg;
        } catch {}
        throw new Error(msg);
      }
      const room = await res.json();
      joinRoom(room, username);
      navigate(`/room/${room.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow mt-8">
      <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Share2 className="w-6 h-6 text-green-600" />
        Room Collaboration
      </h1>
      {/* Only show create form if not auto-joining */}
      {!autoJoin && (
        <form onSubmit={handleCreate} className="mb-6">
          <label className="block mb-2 font-medium">Your Name</label>
          <input
            className="w-full px-3 py-2 border rounded mb-4"
            value={username}
            onChange={e => setUsername(e.target.value)}
            required
          />
          <button
            className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer w-full justify-center"
            type="submit"
            disabled={loading || !username}
          >
            <Plus className="w-4 h-4" />
            {loading ? 'Creating…' : 'Create Room'}
          </button>
        </form>
      )}
      <form onSubmit={handleJoin}>
        <label className="block mb-2 font-medium">Join Room (ID)</label>
        <input
          className="w-full px-3 py-2 border rounded mb-4"
          value={joinRoomId}
          onChange={e => setJoinRoomId(e.target.value)}
          required
          readOnly={autoJoin}
        />
        <label className="block mb-2 font-medium">Your Name</label>
        <input
          className="w-full px-3 py-2 border rounded mb-4"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
          autoFocus={autoJoin}
        />
        <button
          className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer w-full justify-center"
          type="submit"
          disabled={loading || !username || !joinRoomId}
        >
          <LogIn className="w-4 h-4" />
          {loading ? 'Joining…' : 'Join Room'}
        </button>
      </form>
      {error && <div className="text-red-600 mt-4">{error}</div>}
    </div>
  );
}
